import { useMutation } from '@tanstack/react-query';

import { endpoints } from '@/config/apiEndpoints';
import { FIRESTORE_ENABLED } from '@/config/env';
import { apiClient, isForbidden } from '@/lib/apiClient';
import { firebaseApp } from '@/lib/firebase';
import { endFirebaseSession } from './firebaseSession';
import {
  parsePermissions,
  signIn,
  type Membership,
  type Session,
  type UserRole,
} from './session';


export class NoOrganizationError extends Error {
  constructor() {
    super('The session carries no organization to open.');
    this.name = 'NoOrganizationError';
  }
}

interface MembershipResponse {
  organization_id: string;
  role: UserRole;
  permissions: string[];
}

export interface OrganizationChoiceResponse {
  organization_id: string;
  name: string;
  logo_object: string | null;
  small_logo_object: string | null;
  primary_color: string;
  secondary_color: string;
}

export interface SessionResponse {
  token: string;
  name: string;
  email: string;
  organization_id: string | null;
  memberships: MembershipResponse[];
  organizations: OrganizationChoiceResponse[];
}

/**
 * Signs in and mints a fresh ID token, never a cached one.
 *
 * The backend keeps the selected organization on the account's claims and
 * reads the selection off the token presented. A tab that has been open since
 * before the claim was written holds a token from before it, and the popup can
 * complete against that session — so the refresh is forced.
 */
async function requestGoogleIdToken(): Promise<string> {
  const { GoogleAuthProvider, getAuth, signInWithPopup } = await import('firebase/auth');
  const credential = await signInWithPopup(getAuth(firebaseApp()), new GoogleAuthProvider());
  return credential.user.getIdToken(true);
}

/**
 * Re-mints the ID token so the organization claim the backend just wrote is on it.
 *
 * Without this the next API call carries the claim the token was minted before,
 * which is no claim at all, and every endpoint answers 403.
 */
async function refreshIdToken(fallback: string): Promise<string> {
  if (!FIRESTORE_ENABLED) return fallback;
  const { getAuth } = await import('firebase/auth');
  const user = getAuth(firebaseApp()).currentUser;
  return user ? user.getIdToken(true) : fallback;
}

function toMembership(payload: MembershipResponse): Membership {
  return {
    organizationId: payload.organization_id,
    role: payload.role === 'admin' ? 'admin' : 'member',
    permissions: parsePermissions(payload.permissions),
  };
}

function toSession(payload: SessionResponse, token: string): Session {
  return {
    token,
    name: payload.name,
    email: payload.email,
    organizationId: payload.organization_id,
    memberships: payload.memberships.map(toMembership),
  };
}

export interface SignInResult {
  session: Session;
  organizations: OrganizationChoiceResponse[];
}

export interface GoogleSignInOptions {
  onUndecided: (organizations: OrganizationChoiceResponse[]) => void;
}

/**
 * Step one of two: verify the Google sign-in and collect every membership.
 *
 * The backend selects the organization itself when there is exactly one, so the
 * session is stored immediately in that case. With more than one, storing it
 * would sign the app in with nothing to read, so the caller is asked instead.
 */
export function useGoogleSignIn({ onUndecided }: GoogleSignInOptions) {
  return useMutation<SignInResult>({
    mutationFn: async () => {
      const idToken = FIRESTORE_ENABLED ? await requestGoogleIdToken() : null;
      const payload = await apiClient.post<SessionResponse>(endpoints.auth.session(), { idToken });
      const token = payload.organization_id ? await refreshIdToken(payload.token) : payload.token;
      const session = toSession(payload, token);
      if (!session.organizationId && payload.organizations.length < 2) {
        throw new NoOrganizationError();
      }
      return { session, organizations: payload.organizations };
    },
    // A refusal is final, so the Google session it was refused on is ended
    // rather than kept — the next attempt starts from the account chooser.
    onError: async (error) => {
      if (isForbidden(error)) await endFirebaseSession();
    },
    onSuccess: ({ session, organizations }) => {
      if (session.organizationId) {
        signIn(session);
        return;
      }
      onUndecided(organizations);
    },
  });
}

/** Step two: record the choice, then refresh the token so the claim is present. */
export function useSelectOrganization() {
  return useMutation<Session, Error, string>({
    mutationFn: async (organizationId: string) => {
      const payload = await apiClient.post<SessionResponse>(
        endpoints.auth.selectOrganization(),
        { organizationId },
      );
      return toSession(payload, await refreshIdToken(payload.token));
    },
    onSuccess: signIn,
  });
}
